import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { Package, Plus, ArrowDown, ArrowUp, AlertTriangle, X, Lock, Pencil, Trash2 } from 'lucide-react';

export default function Inventario() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const puedeAcceder = ["superadmin","coordinador_general","responsable_inventario"].includes(user?.rol);

  const [tab, setTab] = useState('productos');
  const [productos, setProductos] = useState([]);
  const [entradas, setEntradas] = useState([]);
  const [salidas, setSalidas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [secciones, setSecciones] = useState([]);
  const [estructuras, setEstructuras] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [tipoModal, setTipoModal] = useState('');
  const [editingProductoId, setEditingProductoId] = useState(null);
  const [editingEntradaId, setEditingEntradaId] = useState(null);

  const [formProducto, setFormProducto] = useState({
    nombre_producto: '', categoria: '', unidad_medida: '', stock_minimo: 0, costo_unitario: ''
  });
  const [formEntrada, setFormEntrada] = useState({
    producto_id: '', cantidad: '', proveedor: '', lote: '', observaciones: ''
  });
  const [formSalida, setFormSalida] = useState({
    producto_id: '', cantidad: '', entregado_a_user_id: '', estructura_id: '',
    seccion_id: '', motivo: '', observaciones: ''
  });

  useEffect(() => {
    if (puedeAcceder) fetchData();
    else setLoading(false);
  }, [puedeAcceder]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [prodRes, entRes, salRes, resRes, catRes, estRes] = await Promise.all([
        api.get('/inventario/productos'),
        api.get('/inventario/entradas'),
        api.get('/inventario/salidas'),
        api.get('/inventario/resumen'),
        api.get('/estructuras/catalogos'),
        api.get('/estructuras'),
      ]);
      setProductos(prodRes.data.productos || []);
      setEntradas(entRes.data.entradas || []);
      setSalidas(salRes.data.salidas || []);
      setResumen(resRes.data);
      setSecciones(catRes.data.secciones || []);
      setEstructuras(estRes.data.estructuras || []);
      // Usuarios — solo si tiene permisos (superadmin y coord_general)
      try {
        const usRes = await api.get('/auth/usuarios');
        setUsuarios(usRes.data.usuarios || []);
      } catch {
        setUsuarios([]);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (tipo) => { setTipoModal(tipo); setMostrarModal(true); setMensaje(null); };

  const abrirModalEditarProducto = (p) => {
    setFormProducto({
      nombre_producto: p.nombre_producto || '',
      categoria: p.categoria || '',
      unidad_medida: p.unidad_medida || '',
      stock_minimo: p.stock_minimo || 0,
      costo_unitario: p.costo_unitario || ''
    });
    setEditingProductoId(p.id);
    setTipoModal('producto');
    setMostrarModal(true);
    setMensaje(null);
  };

  const abrirModalEditarEntrada = (e) => {
    setFormEntrada({
      producto_id: e.producto_id,
      cantidad: e.cantidad,
      proveedor: e.proveedor || '',
      lote: e.lote || '',
      observaciones: e.observaciones || ''
    });
    setEditingEntradaId(e.id);
    setTipoModal('entrada');
    setMostrarModal(true);
    setMensaje(null);
  };

  const cerrarModal = () => {
    setMostrarModal(false); setTipoModal('');
    setEditingProductoId(null);
    setEditingEntradaId(null);
    setFormProducto({ nombre_producto: '', categoria: '', unidad_medida: '', stock_minimo: 0, costo_unitario: '' });
    setFormEntrada({ producto_id: '', cantidad: '', proveedor: '', lote: '', observaciones: '' });
    setFormSalida({ producto_id: '', cantidad: '', entregado_a_user_id: '', estructura_id: '', seccion_id: '', motivo: '', observaciones: '' });
  };

  const handleGuardarProducto = async (e) => {
    e.preventDefault();
    try {
      if (editingProductoId) {
        await api.put(`/inventario/productos/${editingProductoId}`, formProducto);
        setMensaje({ tipo: 'exito', texto: 'Producto actualizado correctamente.' });
      } else {
        await api.post('/inventario/productos', formProducto);
        setMensaje({ tipo: 'exito', texto: 'Producto creado correctamente.' });
      }
      fetchData();
      setTimeout(() => cerrarModal(), 1000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al guardar.' });
    }
  };

  const handleGuardarEntrada = async (e) => {
    e.preventDefault();
    try {
      if (editingEntradaId) {
        await api.put(`/inventario/entradas/${editingEntradaId}`, formEntrada);
        setMensaje({ tipo: 'exito', texto: 'Entrada corregida y stock ajustado.' });
      } else {
        await api.post('/inventario/entradas', formEntrada);
        setMensaje({ tipo: 'exito', texto: 'Entrada registrada correctamente.' });
      }
      fetchData();
      setTimeout(() => cerrarModal(), 1000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al registrar.' });
    }
  };

  const handleEliminarEntrada = async (id) => {
    if (!window.confirm('¿Eliminar esta entrada? El stock del producto se ajustará automáticamente.')) return;
    try {
      await api.delete(`/inventario/entradas/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Error al eliminar la entrada.');
    }
  };

  const handleGuardarSalida = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/inventario/salidas', formSalida);
      setMensaje({ tipo: 'exito', texto: `Salida registrada. Folio: ${res.data.folio}` });
      fetchData();
      setTimeout(() => cerrarModal(), 2000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al registrar.' });
    }
  };

  const handleEliminarProducto = async (id) => {
    if (!window.confirm('¿Estás seguro?')) return;
    try {
      await api.delete(`/inventario/productos/${id}`);
      fetchData();
    } catch (error) {
      alert('Error al eliminar.');
    }
  };

  // ── Acceso denegado ──
  if (!puedeAcceder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
          style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
          <img src={logo} alt="DOMINIUM" className="h-10" />
          <button onClick={() => navigate('/dashboard')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
            ← Volver al Dashboard
          </button>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-gray-300 mb-4" size={56} />
          <p className="text-xl font-semibold text-gray-500">Acceso restringido</p>
          <p className="text-gray-400 text-sm mt-2">Solo el Super Administrador puede gestionar el inventario.</p>
          <button onClick={() => navigate('/dashboard')}
            className="mt-6 px-6 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Cargando inventario...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-10" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="text-green-600" size={28} />
            Inventario de Materiales
          </h2>
          <p className="text-gray-500 text-sm">Control de propaganda y materiales de campaña</p>
        </div>

        {resumen && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500">
              <p className="text-xs text-gray-500">Productos</p>
              <p className="text-2xl font-bold text-gray-800">{resumen.productos?.total_productos || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
              <p className="text-xs text-gray-500">Stock Total</p>
              <p className="text-2xl font-bold text-gray-800">{resumen.productos?.stock_total || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500">
              <p className="text-xs text-gray-500">Salidas</p>
              <p className="text-2xl font-bold text-gray-800">{resumen.salidas?.total_salidas || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500">
              <p className="text-xs text-gray-500">⚠ Stock Bajo</p>
              <p className="text-2xl font-bold text-red-600">{resumen.alertas?.sin_stock || 0}</p>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {[
            { key: 'productos', label: '📦 Productos' },
            { key: 'entradas', label: '⬇ Entradas' },
            { key: 'salidas', label: '⬆ Salidas' }
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 font-medium border-b-2 transition ${
                tab === t.key ? 'border-green-600 text-green-600' : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'productos' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg text-gray-800">Catálogo de Productos ({productos.length})</h3>
              <button onClick={() => abrirModal('producto')}
                className="text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                <Plus size={18} /> Nuevo Producto
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Producto</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Categoría</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Unidad</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Stock</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Mínimo</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Estado</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.length === 0 ? (
                    <tr><td colSpan="7" className="py-8 text-center text-gray-400">No hay productos registrados</td></tr>
                  ) : productos.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{p.nombre_producto}</td>
                      <td className="py-3 px-4 capitalize">{p.categoria || '—'}</td>
                      <td className="py-3 px-4">{p.unidad_medida || '—'}</td>
                      <td className="py-3 px-4 font-bold">{p.stock_actual}</td>
                      <td className="py-3 px-4">{p.stock_minimo}</td>
                      <td className="py-3 px-4">
                        {p.stock_actual <= p.stock_minimo ? (
                          <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
                            <AlertTriangle size={14} /> Stock bajo
                          </span>
                        ) : (
                          <span className="text-green-600 text-xs font-medium">✓ OK</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <button onClick={() => abrirModalEditarProducto(p)}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium">
                            <Pencil size={13} /> Editar
                          </button>
                          <button onClick={() => handleEliminarProducto(p.id)}
                            className="flex items-center gap-1 text-red-500 hover:text-red-700 text-xs font-medium">
                            <Trash2 size={13} /> Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'entradas' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg text-gray-800">Entradas de Material ({entradas.length})</h3>
              <button onClick={() => abrirModal('entrada')}
                className="text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                <ArrowDown size={18} /> Registrar Entrada
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Producto</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Cantidad</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Proveedor</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Responsable</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {entradas.length === 0 ? (
                    <tr><td colSpan="6" className="py-8 text-center text-gray-400">No hay entradas registradas</td></tr>
                  ) : entradas.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{e.nombre_producto}</td>
                      <td className="py-3 px-4 text-green-600 font-bold">+{e.cantidad} {e.unidad_medida}</td>
                      <td className="py-3 px-4">{e.proveedor || '—'}</td>
                      <td className="py-3 px-4">{e.responsable_nombre} {e.responsable_apellido}</td>
                      <td className="py-3 px-4">{new Date(e.fecha).toLocaleDateString('es-MX')}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <button onClick={() => abrirModalEditarEntrada(e)}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium">
                            <Pencil size={13} /> Editar
                          </button>
                          <button onClick={() => handleEliminarEntrada(e.id)}
                            className="flex items-center gap-1 text-red-500 hover:text-red-700 text-xs font-medium">
                            <Trash2 size={13} /> Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'salidas' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg text-gray-800">Salidas / Distribución ({salidas.length})</h3>
              <button onClick={() => abrirModal('salida')}
                className="text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                <ArrowUp size={18} /> Registrar Salida
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Folio</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Producto</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Cantidad</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Sección</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Estructura</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Entregado a</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {salidas.length === 0 ? (
                    <tr><td colSpan="7" className="py-8 text-center text-gray-400">No hay salidas registradas</td></tr>
                  ) : salidas.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-xs text-gray-500">{s.folio}</td>
                      <td className="py-3 px-4 font-medium">{s.nombre_producto}</td>
                      <td className="py-3 px-4 text-red-600 font-bold">-{s.cantidad} {s.unidad_medida}</td>
                      <td className="py-3 px-4">{s.numero_seccion || '—'}</td>
                      <td className="py-3 px-4">{s.nombre_estructura || '—'}</td>
                      <td className="py-3 px-4">{s.entregado_nombre ? `${s.entregado_nombre} ${s.entregado_apellido}` : '—'}</td>
                      <td className="py-3 px-4">{new Date(s.fecha).toLocaleDateString('es-MX')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-lg">
                {tipoModal === 'producto' && (editingProductoId ? '✏️ Editar Producto' : '📦 Nuevo Producto')}
                {tipoModal === 'entrada' && (editingEntradaId ? '✏️ Corregir Entrada' : '⬇ Registrar Entrada')}
                {tipoModal === 'salida' && '⬆ Registrar Salida'}
              </h3>
              <button onClick={cerrarModal} className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {mensaje && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
                }`}>{mensaje.texto}</div>
              )}

              {tipoModal === 'producto' && (
                <form onSubmit={handleGuardarProducto} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Producto *</label>
                    <input value={formProducto.nombre_producto} onChange={(e) => setFormProducto({...formProducto, nombre_producto: e.target.value})}
                      placeholder="Ej: Lonas 2x3, Trípticos, Gorras..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                    <select value={formProducto.categoria} onChange={(e) => setFormProducto({...formProducto, categoria: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      <option value="impreso">Impreso</option>
                      <option value="textil">Textil</option>
                      <option value="bardas">Bardas</option>
                      <option value="digital">Digital</option>
                      <option value="otros">Otros</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidad de Medida</label>
                    <select value={formProducto.unidad_medida} onChange={(e) => setFormProducto({...formProducto, unidad_medida: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      <option value="pieza">Pieza</option>
                      <option value="paquete">Paquete</option>
                      <option value="caja">Caja</option>
                      <option value="rollo">Rollo</option>
                      <option value="litro">Litro</option>
                      <option value="kilo">Kilo</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stock Mínimo</label>
                    <input type="number" value={formProducto.stock_minimo} onChange={(e) => setFormProducto({...formProducto, stock_minimo: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Costo Unitario</label>
                    <input type="number" step="0.01" value={formProducto.costo_unitario} onChange={(e) => setFormProducto({...formProducto, costo_unitario: e.target.value})}
                      placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div className="md:col-span-2 flex gap-3 mt-2">
                    <button type="button" onClick={cerrarModal}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">Cancelar</button>
                    <button type="submit"
                      className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>{editingProductoId ? 'Guardar Cambios' : 'Guardar Producto'}</button>
                  </div>
                </form>
              )}

              {tipoModal === 'entrada' && (
                <form onSubmit={handleGuardarEntrada} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Producto *</label>
                    {editingEntradaId ? (
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 text-sm">
                        {productos.find(p => p.id === formEntrada.producto_id)?.nombre_producto || 'Producto'}
                        <span className="block text-xs text-gray-400 mt-0.5">El producto no se puede cambiar al corregir una entrada. Si te equivocaste de producto, elimina esta entrada y registra una nueva.</span>
                      </div>
                    ) : (
                      <select value={formEntrada.producto_id} onChange={(e) => setFormEntrada({...formEntrada, producto_id: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                        <option value="">Seleccionar...</option>
                        {productos.map(p => <option key={p.id} value={p.id}>{p.nombre_producto} (Stock: {p.stock_actual})</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad *</label>
                    <input type="number" value={formEntrada.cantidad} onChange={(e) => setFormEntrada({...formEntrada, cantidad: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
                    <input value={formEntrada.proveedor} onChange={(e) => setFormEntrada({...formEntrada, proveedor: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lote</label>
                    <input value={formEntrada.lote} onChange={(e) => setFormEntrada({...formEntrada, lote: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                    <input value={formEntrada.observaciones} onChange={(e) => setFormEntrada({...formEntrada, observaciones: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div className="md:col-span-2 flex gap-3 mt-2">
                    <button type="button" onClick={cerrarModal}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">Cancelar</button>
                    <button type="submit"
                      className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>{editingEntradaId ? 'Guardar Corrección' : 'Registrar Entrada'}</button>
                  </div>
                </form>
              )}

              {tipoModal === 'salida' && (
                <form onSubmit={handleGuardarSalida} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Producto *</label>
                    <select value={formSalida.producto_id} onChange={(e) => setFormSalida({...formSalida, producto_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                      <option value="">Seleccionar...</option>
                      {productos.map(p => <option key={p.id} value={p.id}>{p.nombre_producto} (Stock: {p.stock_actual})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad *</label>
                    <input type="number" value={formSalida.cantidad} onChange={(e) => setFormSalida({...formSalida, cantidad: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sección Electoral</label>
                    <select value={formSalida.seccion_id} onChange={(e) => setFormSalida({...formSalida, seccion_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      {secciones.map(s => <option key={s.id} value={s.id}>{s.numero_seccion} - {s.municipio}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estructura</label>
                    <select value={formSalida.estructura_id} onChange={(e) => setFormSalida({...formSalida, estructura_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      {estructuras.map(e => <option key={e.id} value={e.id}>{e.nombre_estructura}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entregado a</label>
                    <select value={formSalida.entregado_a_user_id} onChange={(e) => setFormSalida({...formSalida, entregado_a_user_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} {u.apellido_paterno}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                    <select value={formSalida.motivo} onChange={(e) => setFormSalida({...formSalida, motivo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      <option value="distribucion_seccion">Distribución por sección</option>
                      <option value="entrega_promotor">Entrega a promotor</option>
                      <option value="evento">Evento</option>
                      <option value="visita_domiciliaria">Visita domiciliaria</option>
                      <option value="otros">Otros</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                    <input value={formSalida.observaciones} onChange={(e) => setFormSalida({...formSalida, observaciones: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div className="md:col-span-2 flex gap-3 mt-2">
                    <button type="button" onClick={cerrarModal}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">Cancelar</button>
                    <button type="submit"
                      className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>Registrar Salida</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
